/**
 * sessionStorage bridge for surviving the Stripe Checkout redirect.
 *
 * The user uploads files and parses them locally. They click "Screen N
 * Resumes ($X.YZ)" — at that point we have parsed text in React state.
 * We need that state to survive a cross-origin redirect to checkout.stripe.com
 * and back.
 *
 * sessionStorage is the right tool: per-tab, per-origin, persists across
 * navigations within the tab, gone on tab close. Anonymous dump-and-run
 * users get exactly the right scope.
 *
 * Streamlit died on this exact flow last year — server-side session_state
 * doesn't survive a cross-origin trip. React + sessionStorage does.
 */
import {
  ParsedResume,
  CandidateEvaluation,
  UploadResult,
} from './screening.api';

const KEY = 'strix.checkout';

export interface CheckoutStash {
  sessionId: string;
  uploadData: UploadResult;
  customInstructions: string;
  allEvaluations: CandidateEvaluation[];
  count: number;
  amountCents: number;
  stashedAt: number; // Date.now()
}

// Drop stashes older than this. Stripe checkouts time out at 24h; we use
// 1h to be safe and force a re-upload if someone wandered off.
const MAX_AGE_MS = 60 * 60 * 1000;

export function stashCheckout(stash: Omit<CheckoutStash, 'stashedAt'>): void {
  const full: CheckoutStash = { ...stash, stashedAt: Date.now() };
  try {
    sessionStorage.setItem(KEY, JSON.stringify(full));
  } catch (err) {
    console.error('Failed to stash checkout state:', err);
  }
}

export function loadCheckoutStash(): CheckoutStash | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CheckoutStash;
    if (Date.now() - parsed.stashedAt > MAX_AGE_MS) {
      sessionStorage.removeItem(KEY);
      return null;
    }
    return parsed;
  } catch (err) {
    console.error('Failed to load checkout stash:', err);
    return null;
  }
}

export function clearCheckoutStash(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
